import { I18n } from '@lingui/core';
import { Nullable } from '@/types/utils';
import { Language, Locale, NormalisedLocale } from '@/types/common';

export const defaultLocale: NormalisedLocale = 'en-GB';

export const languageNames: Record<Language, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  it: 'Italiano',
  ja: '日本語',
};

export const localesMap: Record<Language, NormalisedLocale> = {
  en: 'en-GB',
  fr: 'fr-FR',
  de: 'de-DE',
  es: 'es-ES',
  it: 'it-IT',
  ja: 'ja-JP',
};

export const languages: Language[] = Object.keys(localesMap);
export const locales = Object.values(localesMap);

export const isKnownLanguage = (value: string): value is Language =>
  languages.includes(value as Language);

export const getLocaleForLanguage = (language: Language) =>
  localesMap[language] || 'en-GB';

export const getLanguageComponent = (locale: Locale = '') =>
  getNormalisedLocale(locale).split('-')[0];

export const getNormalisedLocale = (locale: string): NormalisedLocale => {
  const underscored = locale.replace('-', '_');
  const [language, country = ''] = underscored.split('_');
  return `${language.trim().toLowerCase()}-${country.trim().toUpperCase()}` as NormalisedLocale;
};

export const isSameLocale = (a: Nullable<Locale>, b: Nullable<Locale>) =>
  a && b && getNormalisedLocale(a) === getNormalisedLocale(b);

export const isSameLanguage = (a: Nullable<Locale>, b: Nullable<Locale>) =>
  a && b && getLanguageComponent(a) === getLanguageComponent(b);

type LocalisedValue<T> = { value: Nullable<T>; locale: Nullable<Locale> };

export const getLocalisedDatoValueOrFallback = <T>(
  values: Readonly<Nullable<Nullable<LocalisedValue<T>>[]>>,
  currentLocale: Locale,
  fallbackLocale: Locale = 'en-GB'
) => {
  const localised = values?.find((value) => value?.locale === currentLocale);
  const fallback = values?.find((value) => value?.locale === fallbackLocale);
  return (localised || fallback)?.value;
};

export type TranslatableMessage = (i18n: I18n) => string;

export const translatable =
  (message: string): TranslatableMessage =>
  (i18n) =>
    i18n._(message);
